import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseMutationClearOptions } from 'shared/types/tanstack-utility-types'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { CommentsTypeEnum } from 'modules/comments/types'

interface UseUnlikeCommentOptions extends UseMutationClearOptions<void, Error, boolean> {
  commentId: number
  commentsType: CommentsTypeEnum
  pageId: number
}

export function useUnlikeComment({
  commentId,
  commentsType,
  pageId,
  ...options
}: UseUnlikeCommentOptions): UseMutationResult<void, Error, boolean> {
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.delete, void, void>({
    method: RequestMethodsEnum.delete,
  })

  return useMutation({
    mutationKey: commentsKeys.toggleLike(commentsType, pageId, commentId),
    mutationFn: async () => fetcher(`${COMMENT_API_PREFIX}/${commentId}/like`),
    ...options,
    onSuccess: async (...args) => {
      await options.onSuccess?.(...args)
      await queryClient.refetchQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId), fetchStatus: 'fetching' },
        { cancelRefetch: false },
      )
      void queryClient.invalidateQueries(
        { queryKey: commentsKeys.likes(commentId) },
        { cancelRefetch: false },
      )
      await queryClient.invalidateQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId) },
        { cancelRefetch: false },
      )
    },
  })
}
